/**
 * Canonical Legal Content for MyTownly Rides (Passenger App).
 * Single Source of Truth for:
 *   1. Passenger In-App Legal Viewer  (LegalDocumentScreen)
 *   2. Public Web Legal Pages         (passenger/public/privacy.html, terms.html)
 *
 * When this file changes materially, regenerate the public HTML pages from it
 * and bump PRIVACY_NOTICE_VERSION in passenger/src/config/privacy.js to match.
 *
 * Notice Version: 2026-10-02
 * Last Updated: 2 October 2026
 *
 * NOTE: This is a working policy prepared for launch. It should be reviewed by
 * an advocate familiar with Andhra Pradesh motor-transport and consumer law
 * before it is relied on as the final legal position, in particular the
 * aggregator-licensing, tax and cross-border-transfer statements.
 */

import { LEGAL_DOCS_TE, LEGAL_LAST_UPDATED_TE, LEGAL_VERSION_TE } from './content_te.js';

export const LEGAL_LAST_UPDATED = '2 October 2026';
export const LEGAL_VERSION = '2026-10-02';

const OPERATOR_BLOCK = [
  'MyTownly is currently operated by Shaik Badulla, as sole proprietor, trading as “MyTownly”, at Pileru, Annamayya District, Andhra Pradesh.',
  'We are in the process of incorporating “MyTownly Private Limited”. When incorporation is complete, the business and the responsibilities described in these documents will transfer to that company and this document will be updated.',
  'Registered / operating address: JWXV+8J3, Kotha Palli Road, Bodumalluvaripalle, Pileru, Annamayya District, Andhra Pradesh – 517214.',
  'Grievance Officer & Privacy Contact: Shaik Badulla.',
  'Phone / WhatsApp: +91 79890 20222 · Email: hello@mytownly.in',
];

export const LEGAL_DOCS = {
  privacy: {
    title: 'Privacy Policy',
    subtitle: 'How MyTownly collects, uses, shares, and protects your personal data when you use the MyTownly Passenger App.',
    lastUpdated: LEGAL_LAST_UPDATED,
    version: LEGAL_VERSION,
    sections: [
      {
        id: 'about',
        title: '1. Who Is Responsible for Your Data',
        content: [
          'MyTownly is a technology platform that connects passengers with independent auto-rickshaw drivers (“Annas”) in and around Pileru, Andhra Pradesh, India.',
          'For the purposes of India’s Digital Personal Data Protection Act, 2023 (“DPDP Act”), the Data Fiduciary for the MyTownly Passenger App is:',
          ...OPERATOR_BLOCK,
        ],
      },
      {
        id: 'collected',
        title: '2. Information We Collect',
        content: [
          'We collect only the information reasonably necessary to provide rides, keep you safe, operate your account, and meet our legal obligations.',
        ],
        table: {
          headers: ['Information', 'Why we use it'],
          rows: [
            ['Mobile number', 'OTP login, account security, ride and safety updates'],
            ['Name (and, if you book for someone else, their name)', 'Identifying the rider to the Anna during a trip'],
            ['Pickup and drop locations, saved places (home / work)', 'Booking, routing, fare calculation, and driver matching'],
            ['Device location (while the app is open)', 'Setting your pickup point and showing nearby Annas'],
            ['Trip history — status, route, fare, ratings, in-app chat', 'Receipts, ratings, support, disputes, safety, and required records'],
            ['Cancellation and no-show history', 'Applying our published cancellation policy fairly'],
            ['Push notification token', 'Ride and safety notifications'],
            ['Emergency contacts (optional)', 'SOS and trip-safety alerts'],
            ['Consent records and privacy preferences', 'Showing what you agreed to and when'],
            ['Device and app information, approximate location', 'Fraud prevention, diagnostics, and app-usage analytics'],
          ],
        },
      },
      {
        id: 'usage',
        title: '3. How We Use Your Data',
        content: [
          'Your data may be used to:',
          '• Create and secure your account and verify your phone number.',
          '• Match you with an available Anna and set or update your pickup location.',
          '• Calculate routes, show an upfront fare, and support the live trip.',
          '• Send booking, arrival, trip-status, and safety notifications.',
          '• Apply our cancellation policy, including assessing and later clearing any cancellation fee (see the Terms of Service).',
          '• Process an optional fare boost that you voluntarily add to a booking.',
          '• Provide customer support and handle ratings, feedback, and disputes.',
          '• Detect and prevent fraud, abuse, and security threats.',
          '• Operate SOS and other safety features.',
          '• Keep the business, accounting, safety, and tax records we are required to keep, and comply with applicable law.',
          '• Understand app usage in aggregate and improve the service.',
          'We do not sell, rent, or trade your personal data to advertisers or data brokers.',
        ],
      },
      {
        id: 'legal_basis',
        title: '4. Our Legal Basis',
        content: [
          '• Most ride, account, and safety processing is necessary to provide the service you ask for and to meet obligations that apply to us.',
          '• Optional features — such as adding emergency contacts, analytics, or promotional messages — are based on your consent, which you can withdraw at any time.',
          '• Some records (for example trip and tax records) are kept because the law requires us to keep them for a period.',
        ],
      },
      {
        id: 'location',
        title: '5. Location and GPS',
        content: [
          'Location is central to the MyTownly experience.',
          '• Your location: We request location access only while you are using location features — the booking map, showing nearby Annas, routing, and an active trip. We do NOT track your location in the background after you close the app.',
          '• Pickup pin: The pickup point you confirm is shared with your assigned Anna.',
          '• The moving auto on your map during a trip is your Anna’s live location, relayed to you for the duration of that trip. It is not your location.',
          'If you deny location permission, some features may not work correctly.',
        ],
      },
      {
        id: 'driver_info',
        title: '6. Information About Your Anna',
        content: [
          'When a ride is assigned, we show limited information so you can identify your ride and travel safely:',
          '• Anna name, profile photo, and rating.',
          '• Vehicle model, colour, and registration number.',
          '• The Anna’s live location on the map during the active trip.',
        ],
      },
      {
        id: 'shared_with_driver',
        title: '7. Information Shared With Your Anna',
        content: [
          'To make the ride possible, we share with your assigned Anna:',
          '• Your name (or the rider’s name), pickup location and address, and drop location.',
          '• Relevant trip information and any messages you send through in-app chat.',
          'Your personal phone number is not intentionally exposed to the Anna.',
        ],
      },
      {
        id: 'otp',
        title: '8. Trip-Start OTP',
        content: [
          'Every ride is protected by a 4-digit trip-start OTP shown to you in the app.',
          'The Anna must obtain the OTP from you and enter it to start the ride. Share it only with your assigned Anna, and only when you are safely in the correct vehicle.',
        ],
      },
      {
        id: 'safety',
        title: '9. Safety Features and Our Safety Team',
        content: [
          '• SOS: When you activate SOS during an eligible ride, we process your trip and location information to trigger a safety response. Your saved emergency contacts receive an alert with a live tracking link, and the alert is also sent to MyTownly’s internal safety and operations team through a private monitoring channel so we can act on it.',
          '• Emergency helpline: Where your device supports it, the app provides one-tap access to India’s emergency number 112. We are working to connect the in-app panic button directly to official emergency-response services.',
          '• Live trip sharing: You can generate a temporary tracking link and share it with someone you trust. The link does not expose your phone number and expires when the ride ends.',
          '• Emergency contacts: You may add up to three, and remove them at any time.',
        ],
      },
      {
        id: 'cancellation_data',
        title: '10. Cancellation-Fee Data',
        content: [
          'If a cancellation fee applies to your account under our published policy (see the Terms of Service), we store the outstanding amount and a count of recent late cancellations on your account.',
          'The outstanding amount is shown in the fare of your next booking and is cleared once that ride is completed. We keep a record of the fee having been applied for dispute and audit purposes.',
        ],
      },
      {
        id: 'payments',
        title: '11. Payments',
        content: [
          'MyTownly rides are paid in cash directly to the Anna after the trip. We do not collect or store your bank account, card, or UPI details.',
          'Any UPI identifier shown in the app is used by Annas to settle amounts they owe to MyTownly. It is not a way for you to pay us, and you should not send money to it.',
        ],
      },
      {
        id: 'third_parties',
        title: '12. Service Providers and Where Your Data Is Processed',
        content: [
          'We use trusted providers to run the app:',
          '• Google Firebase — authentication, database, file storage, hosting, cloud functions, and push notifications. Your account, ride records, photos, and cloud functions are hosted in Google’s Mumbai, India region.',
          '• Google Firebase Realtime Database — live trip status and the live location relay during a trip. This component is currently hosted in Google’s Singapore region, so live-trip data is processed outside India for the duration of the trip.',
          '• Google Analytics for Firebase — aggregate app-usage measurement, processed on Google’s global infrastructure.',
          '• Google Maps Platform — maps, places, geocoding, and routing.',
          '• Google reCAPTCHA — protection against automated abuse during login.',
          '• Truecaller (Android) — faster phone verification, only where this option is enabled in the app; OTP login is always available as an alternative.',
          '• Your device manufacturer’s push service — delivering notifications.',
          'Cross-border transfers are made only to countries not restricted by the Government of India under the DPDP Act. These providers process only what is needed to deliver their service, and we do not sell your personal data.',
        ],
      },
      {
        id: 'retention',
        title: '13. Data Retention',
        content: [
          'We keep personal data only as long as needed for the purpose it was collected, and then delete or anonymise it. Our standard periods are:',
          '• Account profile: until your account is deleted.',
          '• Ride records (route, fare, ratings, in-app chat): about 3 years, for receipts, disputes, safety, and tax.',
          '• SOS and safety-alert logs, and stored location logs: about 90 days.',
          '• Live GPS points during a trip: purged from the live database within about 30 minutes of the trip ending.',
          '• Login and security logs: about 1 year.',
          '• After account deletion: your direct identifiers are removed within 30 days; any ride facts we must retain for the periods above are kept in de-identified form.',
        ],
      },
      {
        id: 'security',
        title: '14. Data Security',
        content: [
          'We use reasonable technical and organisational safeguards — server-side validation of every ride action, access rules, encrypted transport (HTTPS), and rate limiting — to protect your data.',
          'No internet service can be perfectly secure. If a personal-data breach occurs that requires notification under applicable law, we will notify the Data Protection Board of India and affected users as required.',
        ],
      },
      {
        id: 'rights',
        title: '15. Your Rights',
        content: [
          'Subject to applicable law, you have the right to:',
          '• Access a summary of the personal data we hold about you and how it is processed.',
          '• Correct or update inaccurate or incomplete data.',
          '• Request erasure of your personal data, subject to records we must keep by law.',
          '• Withdraw consent for any optional processing.',
          '• Nominate another person to exercise your rights in the event of death or incapacity.',
          '• Raise a grievance and have it addressed.',
          'Use Profile → Privacy & Legal in the app, or contact the Grievance Officer below. We may verify your identity before acting on a request, and we aim to respond within 30 days.',
        ],
      },
      {
        id: 'deletion',
        title: '16. Deleting Your Account',
        content: [
          'You can permanently delete your MyTownly account from:',
          'Profile → Privacy & Legal → Delete Account',
          'You can also email hello@mytownly.in for help. When your account is deleted we remove your login and profile and clear stored location data; ride facts that must be retained for law, tax, safety, or fraud prevention are kept in de-identified form for the periods listed in Section 13.',
        ],
      },
      {
        id: 'children',
        title: '17. Children',
        content: [
          'MyTownly is intended for users aged 18 and over. You confirm you are 18 or older when you create an account.',
          'We do not knowingly collect the personal data of children in breach of applicable law. If you believe a child has provided us personal data, contact hello@mytownly.in and we will delete it.',
        ],
      },
      {
        id: 'changes',
        title: '18. Changes to This Policy',
        content: [
          'We may update this Privacy Policy when our service, technology, or legal requirements change.',
          'If we make significant changes we will give notice in the app or by another suitable method. The “Last updated” date and version at the top show the current version.',
        ],
      },
      {
        id: 'grievance',
        title: '19. Grievance Redressal and Contact',
        content: [
          'For any privacy question, data request, or grievance, contact our Grievance Officer:',
          ...OPERATOR_BLOCK,
          'We aim to acknowledge grievances within 48 hours and resolve them within 30 days.',
        ],
      },
    ],
  },

  terms: {
    title: 'Terms of Service',
    subtitle: 'The rules, responsibilities, fares, and cancellation policy that apply when you use MyTownly.',
    lastUpdated: LEGAL_LAST_UPDATED,
    version: LEGAL_VERSION,
    sections: [
      {
        id: 'platform',
        title: '1. About MyTownly and These Terms',
        content: [
          'MyTownly is a technology platform that connects passengers with independent auto-rickshaw drivers (“Annas”) in and around Pileru, Andhra Pradesh.',
          'MyTownly provides the booking technology. The transportation is provided by the independent Anna operating the vehicle. MyTownly is not a transport operator and does not own or operate auto-rickshaws.',
          'These Terms form an agreement between you and the operator of MyTownly:',
          ...OPERATOR_BLOCK,
          'By creating an account, requesting a ride, or otherwise using MyTownly, you agree to these Terms and to the Privacy Policy. If you do not agree, do not use the service.',
          'MyTownly operates in accordance with applicable Indian law. Where a licence or authorisation is required for aggregator operations in Andhra Pradesh, we will obtain it as required; fares follow the auto-rickshaw tariff applicable in the area.',
        ],
      },
      {
        id: 'eligibility',
        title: '2. Eligibility and Your Account',
        content: [
          '• You must be 18 years or older to create an account and book rides.',
          '• You must provide a valid Indian mobile number that can receive OTPs, and accurate account information.',
          '• You are responsible for activity on your account and for keeping your device and access secure.',
          '• One account per person. Do not use another person’s account or use MyTownly for unlawful or fraudulent purposes.',
          '• We may suspend or restrict an account that breaches these Terms or creates a safety or security risk.',
        ],
      },
      {
        id: 'service',
        title: '3. What MyTownly Does and Does Not Do',
        content: [
          '• MyTownly matches your ride request with an available Anna and provides trip technology (map, OTP, chat, live tracking, safety tools).',
          '• MyTownly does not provide the transport itself, does not employ Annas, and does not guarantee that an Anna will be available for any request.',
          '• Estimated arrival times and fares are estimates and may change with distance, traffic, demand, and conditions.',
        ],
      },
      {
        id: 'fares',
        title: '4. Booking, Fares and Payment',
        content: [
          '• Request a ride by entering your pickup and destination. The app shows the applicable fare before you confirm.',
          '• The fare may be made up of a base fare, a minimum fare, distance-based charges, and other charges permitted under the applicable tariff. Applicable taxes are included where they apply.',
          '• Payment is in cash directly to the Anna after the trip. Tolls, parking, or similar legitimate charges are payable by you where not already shown.',
          '• Fare boost (optional): you may voluntarily add an extra amount to your booking to attract an Anna faster during busy periods. This amount is part of the cash fare you pay the Anna. It is chosen by you — MyTownly does not apply surge pricing on your behalf.',
          '• Waiting charge (when shown on the booking screen): if your Anna has reached your pickup point and you keep him waiting, the first few minutes are free; after that a small per-minute charge applies, subject to a maximum amount per booking. The free time and the rate are shown on the booking screen before you confirm, and by booking you accept them. The wait is timed from when your Anna arrives at your pickup point until your trip PIN is accepted. The whole waiting charge is added to the fare and paid in cash to the Anna. It applies only to bookings where these terms were shown to you.',
          '• If no Anna accepts your request, it keeps searching and is automatically cancelled after about 8 minutes (or sooner if you have left it idle) so you can simply book again. No fee applies to a search that never found an Anna.',
        ],
      },
      {
        id: 'cancellations',
        title: '5. Cancellations, No-Shows and Cancellation Fees',
        content: [
          'You can cancel a ride request at any time before the trip starts. Our cancellation policy is designed to be fair to both you and the Anna who is on the way to you:',
          '• Free: cancelling before any Anna accepts, or within 2 minutes of an Anna accepting.',
          '• First late cancellation after that window: free, with a one-time reminder that a fee will apply next time.',
          '• Each further late cancellation (an Anna had already accepted and was on the way or had arrived): a ₹10 fee, up to a maximum of ₹20 outstanding at any time.',
          '• Any outstanding cancellation amount is added to the fare of your next ride and collected in cash by that Anna. It is cleared from your account once that ride is completed.',
          '• Genuine reasons matter: contact support if a fee was applied unfairly (for example the Anna was not moving, asked you to cancel, or could not reach you).',
          '• Repeated abusive cancellations, fraudulent bookings, or deliberate no-shows may lead to temporary or permanent restrictions on your account.',
          'Nothing in this section limits any right you have under the Consumer Protection Act, 2019 or other law that cannot be excluded.',
        ],
      },
      {
        id: 'responsibilities',
        title: '6. Your Responsibilities as a Passenger',
        content: [
          'When using MyTownly you agree to:',
          '• Enter accurate pickup and drop details and be ready at your pickup point.',
          '• Check that the Anna, vehicle, and registration number match the app before boarding.',
          '• Share the trip-start OTP only with your assigned Anna.',
          '• Treat the Anna and others with respect — no threats, harassment, abuse, or discrimination.',
          '• Not damage the vehicle or property, and not carry prohibited, hazardous, or illegal items.',
          '• Not manipulate bookings, fares, ratings, or other platform features.',
        ],
      },
      {
        id: 'drivers_safety',
        title: '7. The Anna, the Vehicle and Safety',
        content: [
          '• Annas are independent operators responsible for holding a valid driving licence, vehicle permit, fitness certificate, and insurance, and for complying with applicable law.',
          '• MyTownly carries out onboarding checks (identity and vehicle photos, registration details, platform briefing) to help you identify your Anna, but MyTownly is not the transport provider.',
          '• Use SOS only for genuine emergencies. SOS alerts your emergency contacts and MyTownly’s safety team; where supported, the app also gives one-tap access to 112.',
          '• If you feel unsafe, end the ride at a safe place and contact support or emergency services.',
        ],
      },
      {
        id: 'ratings',
        title: '8. Ratings and Feedback',
        content: [
          'After a ride you can rate your Anna and leave feedback. Ratings help keep the service safe and reliable. Do not submit false, abusive, or retaliatory ratings.',
        ],
      },
      {
        id: 'prohibited',
        title: '9. Prohibited Conduct',
        content: [
          'You must not use MyTownly to:',
          '• Commit or facilitate any illegal activity.',
          '• Threaten, harass, abuse, or harm another person.',
          '• Provide false information or impersonate someone.',
          '• Abuse promotions or create multiple or fake accounts.',
          '• Interfere with, scrape, reverse-engineer, or attempt to compromise the platform.',
          '• Access another person’s account without authorisation.',
        ],
      },
      {
        id: 'termination',
        title: '10. Suspension and Termination',
        content: [
          '• You may stop using MyTownly at any time and delete your account.',
          '• We may suspend, restrict, or terminate access if we reasonably believe you have breached these Terms, engaged in fraud or abuse, created a safety or security risk, or used the platform unlawfully.',
          '• Where appropriate we will review the circumstances before restoring access, and any outstanding lawful amounts remain payable.',
        ],
      },
      {
        id: 'liability',
        title: '11. Service Availability, Disclaimers and Liability',
        content: [
          '• We work to keep MyTownly reliable but cannot guarantee uninterrupted service. It may be affected by network, GPS, device, third-party, weather, traffic, or other factors outside our reasonable control.',
          '• The transport service is provided by the independent Anna, who is responsible for the conduct of the trip and the vehicle.',
          '• To the maximum extent permitted by law, MyTownly is not liable for indirect or consequential losses, or for matters beyond our reasonable control or arising from independent third-party services.',
          '• Where MyTownly is found liable despite the above, our total liability is limited to the greater of the fare for the ride concerned or ₹1,000, except where the law does not allow such a limit.',
          '• Nothing in these Terms excludes or limits any liability or consumer right that cannot lawfully be excluded or limited.',
        ],
      },
      {
        id: 'ip',
        title: '12. Intellectual Property',
        content: [
          'The MyTownly app, website, brand, the names “MyTownly” and “Anna” as used here, logos, and content are owned by MyTownly or its licensors. You may use them only to use the service as intended. Do not copy, modify, distribute, or create derivative works without permission.',
        ],
      },
      {
        id: 'grievance',
        title: '13. Grievance Redressal',
        content: [
          'In accordance with applicable Indian law, including the Consumer Protection (E-Commerce) Rules, 2020 and the Information Technology Rules, our Grievance Officer is:',
          ...OPERATOR_BLOCK,
          'We aim to acknowledge complaints within 48 hours and resolve them within 30 days.',
        ],
      },
      {
        id: 'changes',
        title: '14. Changes to These Terms',
        content: [
          'We may update these Terms when our service, features, pricing, or legal requirements change. If we make significant changes we will give notice in the app. The “Last updated” date and version at the top show the current version. Continuing to use MyTownly after a change means you accept the updated Terms.',
        ],
      },
      {
        id: 'governing_law',
        title: '15. Governing Law and Disputes',
        content: [
          'These Terms are governed by the laws of India.',
          'Subject to the rights below, the courts at Pileru, Annamayya District, Andhra Pradesh have jurisdiction over disputes, and where those courts lack jurisdiction, the District Court at Rayachoti, Andhra Pradesh.',
          'If you are a consumer, nothing above prevents you from bringing a complaint before the consumer forum having jurisdiction where you reside or work, as permitted by the Consumer Protection Act, 2019.',
          'We encourage you to contact support first so we can try to resolve the matter directly.',
        ],
      },
      {
        id: 'contact',
        title: '16. Contact',
        content: [
          'MyTownly · Email: hello@mytownly.in · Phone / WhatsApp: +91 79890 20222 · JWXV+8J3, Kotha Palli Road, Bodumalluvaripalle, Pileru, Annamayya District, Andhra Pradesh – 517214.',
        ],
      },
    ],
  },

  safety: {
    title: 'Safety & Emergency Guidelines',
    subtitle: 'How MyTownly helps keep every ride safe — and what to do in an emergency.',
    lastUpdated: LEGAL_LAST_UPDATED,
    version: LEGAL_VERSION,
    sections: [
      {
        id: 'otp_verification',
        title: '1. 4-Digit Trip-Start OTP',
        content: [
          'Every ride has a unique 4-digit start OTP shown on your screen.',
          'Check that the auto registration number matches the app before boarding, and share the OTP only once you are safely inside the correct vehicle.',
        ],
      },
      {
        id: 'sos_feature',
        title: '2. Emergency SOS',
        content: [
          'The SOS button is available during any active ride. When you press it:',
          '• Your live location and trip details are captured and an urgent alert is raised.',
          '• Your saved emergency contacts receive a WhatsApp/SMS alert with a live tracking link.',
          '• The alert is sent to MyTownly’s safety and operations team through a private monitoring channel so we can respond.',
          '• Where your device supports it, you also get one-tap access to 112 (police) and 108 (ambulance).',
          'We are working to connect the panic button directly to official emergency-response services.',
        ],
      },
      {
        id: 'trip_share',
        title: '3. Live Trip Sharing',
        content: [
          'Tap “Share Trip” on the active ride screen to create a secure, temporary link.',
          'Whoever you send it to can watch your auto move on the map in real time without installing the app. The link does not reveal your phone number and expires when the ride ends.',
        ],
      },
      {
        id: 'verified_captains',
        title: '4. Verified Local Annas',
        content: [
          'Annas complete onboarding checks including identity and vehicle photos, registration details, and a platform briefing before they can take rides. Annas are independent operators responsible for their licence, permit, and insurance.',
        ],
      },
      {
        id: 'good_practice',
        title: '5. Good Safety Practice',
        content: [
          '• Wait for your ride in a safe, well-lit place.',
          '• Match the Anna, photo, vehicle model, and number plate to the app before getting in.',
          '• Share your trip with a family member for evening or unfamiliar routes.',
          '• Trust your instincts — if something feels wrong, end the ride at a safe location and call for help.',
        ],
      },
    ],
  },
};

export { LEGAL_DOCS_TE, LEGAL_LAST_UPDATED_TE, LEGAL_VERSION_TE };

export const getLegalDoc = (docType = 'privacy', lang = 'en') => {
  const docs = lang === 'te' ? LEGAL_DOCS_TE : LEGAL_DOCS;
  return docs?.[docType] || LEGAL_DOCS[docType] || docs?.privacy || LEGAL_DOCS.privacy;
};

